<template>
  <div class="owal-poster-frame__outer-container" :class="{modal: showModal}">
    <div
      v-if="showModal"
      class="owal-poster-frame__modal-image-container"
      @click.self="showModal = false"
      >
      <owal-transition-slide from="top">
        <div
          v-if="showModal"
          slot="content"
          class="owal-poster-frame__modal-inner-container"
          >
          <div class="owal-poster-frame__modal-image-close-button" @click="showModal = false">
            x
          </div>
          <img :src="posterFrameLarge" class="owal-poster-frame__modal-image">
        </div>
      </owal-transition-slide>
    </div>
    <div class="owal-poster-frame__confidence-container">
      <owal-indicator-confidence
        v-if="faceConfidence"
        :confidence="faceConfidence"
        :confidence-level="matchConfidenceLevel"
        />
    </div>
    <div :class="`owal-poster-frame__image-small-${frameStyle}`">
      <div class="owal-poster-frame__link" @click="showModal = true">
        <img
          v-if="frameStyle === 'pile'"
          v-lazy="thumbnail"
          class="owal-poster-frame__image-small frame0"
          >
        <img
          v-if="frameStyle === 'pile'"
          v-lazy="thumbnail"
          class="owal-poster-frame__image-small frame2"
          >
        <img v-lazy="thumbnail" :key="thumbnail" class="owal-poster-frame__image-small">
      </div>
    </div>
    <div v-if="this.posterFrameLarge" class="owal-poster-frame__image-large-outer-container">
      <div class="owal-poster-frame__image-large-inner-container">
        <div class="owal-poster-frame__large-content_top">
          <div class="owal-poster-frame__top_text">
            <div class="owal-poster-frame__title">
              {{ title }}
            </div>
            <div class="owal-poster-frame__subtitle">
              {{ subtitle }}
            </div>
          </div>
          <div class="owal-poster-frame__button">
            <owal-banner-button v-if="shouldShowShapesToggle" v-bind="claheButton"/>
          </div>
        </div>
        <span class="owal-poster-frame__image-large-content_center" @click="showModal = true">
          <img v-lazy="this.posterFrameLarge" class="owal-poster-frame__image-large-content_center">
        </span>
        <div class="owal-poster-frame__large-content_bottom">
          <owal-banner-button
            v-for="button in buttons"
            :key="button.identifier"
            v-bind="button"
            />
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import OwalIndicatorConfidence from '../indicators/OwalIndicatorConfidence'
import { imageForDetectionID } from '@/libraries/api/faces'
import OwalBannerButton from '../buttons/OwalBannerButton'
import { mapGetters, mapActions } from 'vuex'
import { OwalTransitionSlide } from '../transitions/'

export default {
  name: 'OwalBannerPosterFrame',
  components: {
    OwalIndicatorConfidence,
    OwalBannerButton,
    OwalTransitionSlide,
  },
  props: {
    /**
     * The thumbnail that is displayed as the poster frame.
     */
    posterFrameSmall: {
      type: String,
    },
    /**
     * A URL pointing to the full frame.
     */
    posterFrameLarge: {
      type: String,
    },
    /**
     * The visual appearance of the poster frame.
     */
    frameStyle: {
      type: String,
      default: 'one',
    },
    /**
     * The title of the poster frame.
     */
    title: {
      type: String,
    },
    /**
     * The subtitle of the poster frame.
     */
    subtitle: {
      type: String,
    },
    /**
     * A number between 0 and 1, indicating how confident the system is that it detected a face.
     */
    faceConfidence: {
      type: Number,
      required: false,
      default: undefined,
    },
    /**
     * Do we want to show shapes (such as 5 points) on faces?
     */
    shapes: {
      type: Boolean,
      default: false,
    },
    /**
     * Do we use histogram equalization?
     */
    clahe: {
      type: Boolean,
      default: false,
    },
    /**
     * Used to show some buttons on the poster frame callouts.
     */
    detectionId: {
      type: String,
      default: undefined,
    },
    buttons: {
      type: Array,
      default: function () {
        return []
      },
    },
    /**
     * Intentionally hidden with no toggle for turning on
     */
    shouldShowShapesToggle: {
      type: Boolean,
      default: false,
    },
    matchConfidenceLevel: {
      type: Number,
      required: false,
      default: undefined,
    },
  },
  data () {
    return {
      showModal: false,
    }
  },
  computed: {
    ...mapGetters({
      filters: 'trainingTool/filters',
    }),
    thumbnail () {
      let url = imageForDetectionID(this.detectionId)

      if (this.posterFrameSmall) {
        url = this.posterFrameSmall
      }

      if (this._shapes) {
        url = `${url}?shape=1`
        if (this._clahe) {
          url = `${url}&clahe=1`
        }
      }
      return url
    },
    _shapes: {
      get () {
        return this.filters && this.filters.shapesOnFaces
      },
      set (newValue) {
        this.updateFilters({
          shapesOnFaces: newValue,
        })
      },
    },
    _clahe: {
      get () {
        return this.filters.clahe
      },
      set (newValue) {
        this.updateFilters({
          clahe: newValue,
        })
      },
    },
    claheButton () {
      const button = {
        identifier: 'shapes',
        title: this._shapes ? 'Turn Shapes Off' : 'Turn Shapes On',
        callback: () => {
          const shouldShowShapes = !!this._shapes
          this._shapes = !shouldShowShapes // eslint-disable-line
        },
        accessoryType: 'none',
      }
      return button
    },
  },
  methods: {
    ...mapActions({
      updateFilters: 'trainingTool/updateFilters',
    }),
    imageForDetectionID,
  },
}
</script>

<style scoped>

/** --- Container --- */
.owal-poster-frame__outer-container {
  display: flex;
  flex-direction: row;
  flex-shrink: 0; /* Prevent outside contexts from shrinking the frame. */
  align-items: center;
  text-align: center;
  width: 16rem;
  height: 9rem;
  transition: all 250ms linear;
  z-index: 99;
  position: relative;
}

.owal-poster-frame__image-small-one,
.owal-poster-frame__image-small-pile {
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  position: relative;
  width: 100%;
  height: 100%;
  padding: 0;
  transition: transform 200ms ease;
}

.owal-poster-frame__image-small-pile:hover .owal-poster-frame__image-small,
.owal-poster-frame__image-small-one:hover .owal-poster-frame__image-small {
  border: 3px solid #007aee;
  cursor: pointer;
}

/** Frame 0 and Frame 2 are the decoration frames. */
.frame0 {
  transform: rotate(-5deg);
}
.frame2 {
  transform: rotate(5deg);
}

.owal-poster-frame__image-small[lazy=loading],
.owal-poster-frame__image-small[lazy=error],
.owal-poster-frame__image-large img[lazy=loading],
.owal-poster-frame__image-large img[lazy=error] {
  background: #fff;
}

.owal-poster-frame__image-small[lazy=error] {
  border-color: orangered;
}

/** The frame image itself, */
.owal-poster-frame__image-small {
  position: absolute;
  background: #000;
  /* seperate because we want to animate with CSS animations. */
  border-color: #fff;
  border-style: solid;
  border-width: 1px;
  border-radius: .35rem;
  object-fit: contain;
  padding: 0;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  transition: border-color 250ms ease, border-width 250ms ease;
}

.owal-poster-frame__confidence-container {
  position: absolute;
  align-self: flex-end;
  z-index: 11;
  width: 100%;
  display: flex;
  flex-direction: row-reverse;
  flex-shrink: 0;
  margin-top: 0.5rem;
  pointer-events: none;
  transition: opacity 300ms ease;
  opacity: 1;
}

.owal-poster-frame__outer-container:hover .owal-poster-frame__confidence-indicator {
  opacity: 0;
}

/** --- Callout on Hover --- */
.owal-poster-frame__image-large-outer-container {
  position: relative;
  visibility: hidden;
  display: flex;
  flex-direction: column;
  opacity: 0;
  border-radius: .35rem;
  background: none;
  flex-shrink: 0;
  z-index: 10;
  transform: scale(0.1);
  transform-origin: 0 50%;
  transition: margin 400ms ease, opacity 250ms ease, transform 400ms ease;
  padding: 1rem;
  flex-grow: 0;
}
/** --- Implement callout --- */

.owal-poster-frame__image-small-pile:hover + .owal-poster-frame__image-large-outer-container,
.owal-poster-frame__image-small-one:hover + .owal-poster-frame__image-large-outer-container,
.owal-poster-frame__image-large-outer-container:hover {
  visibility: visible;
  transform: translate(0, 0) scale(1.1);
  opacity: 1;
  height: 25.25rem;
  width: 30.25rem;
}

/** --- Callout contents --- */
.owal-poster-frame__image-large-inner-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: #D8D8D8;
  border: 1px solid #475669;
  box-shadow: 0 .35rem 1.42rem 0 rgba(0, 0, 0, 0.5);
  border-radius: 0.35rem;
  padding: 0;
  margin: 0;
  border-collapse: collapse;
}

.owal-poster-frame__large-content_top {
  display: flex;
  flex-shrink: 0;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border-radius: .35rem;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  background: #D8D8D8;
}

.owal-poster-frame__top_text {
  display: flex;
  flex-direction: column;
}

.owal-poster-frame__button {
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
}

.owal-poster-frame__image-large-content_center {
  object-fit: contain;
  background: #475669;
  height: 16rem;
  width: 100%;
  cursor: pointer;
}

.owal-poster-frame__large-content_bottom {
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  flex-grow: 0;
  justify-content: flex-start;
  align-content: stretch;
  align-items: stretch;
  width: 100%;
  border-radius: .35rem;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  padding: 0.5rem 0.5rem;
}

.owal-poster-frame__large-content_bottom * {
  margin: 0 0.05rem;
  flex-grow: 1;
}

/** Title and Subtitle */
.owal-poster-frame__title {
  font-family: 'Lato-Bold', sans-serif;
  font-weight: bold;
  font-size: 1rem;
  color: #475669;
  letter-spacing: 0;
  text-align: left;
  white-space: nowrap;
  text-overflow: ellipsis;
  width: 100%;
  display: flex;
  transition: all 250ms linear;
  flex-grow: 0;
}

.owal-poster-frame__subtitle {
  display: flex;
  font-family: 'Lato-Italic', sans-serif;
  font-size: 0.8571rem;
  color: #8492A6;
  letter-spacing: 0;
  text-align: left;
  width: 100%;
}

/** --- Modal Overlay --- */
.owal-poster-frame__modal-image-container {
  position: fixed;
  height: 100%;
  width: 100%;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  background: #000000aa;
  z-index: 900;
  display: flex;
  justify-content: center;
  align-content: center;
  padding: 2.5%;
  cursor: pointer;
}

.owal-poster-frame__modal-inner-container {
  object-fit: contain;
  margin: 10rem;
  padding: 0;
  max-height: 90%;
  max-width: 90%;
  width: fit-content;
  z-index: 900;
  cursor: initial;
}

.owal-poster-frame__modal-image {
  object-fit: contain;
  border-radius: 0.35rem;
  background: #000;
  height: 100%;
  max-width: 100%;
  width: fit-content;
  z-index: 800;
  cursor: initial;
  border: 2px solid #fff;
}

.owal-poster-frame__modal-image-close-button {
  position: relative;
  color: #fff;
  background: #000;
  align-self: center;
  display: flex;
  justify-content: center;
  align-content: center;
  align-items: center;
  height: 2rem;
  width: 2rem;
  flex-grow: 0;
  flex-shrink: 0;
  text-align: center;
  padding: 1rem;
  border-radius: 3rem;
  margin-top: -1rem;
  margin-bottom: -1rem;
  margin-left: -1rem;
  font-weight: bold;
  cursor: pointer;
  border: 2px solid rgba(0, 0, 0, 0);
  line-height: 1;
  transition: all 300ms ease;
  z-index: 905;
}

.owal-poster-frame__modal-image-close-button:hover {
  border: 2px solid rgba(255, 255, 255, 1);
  background: #333;
  z-index: 905;
}

</style>
